import { useRef, useState, useEffect } from 'react';
import { Waypoint } from 'react-waypoint';
import { Button, Cell, EmptyState, DeprecatedIcon } from '@vinted/web-ui';
import { includes } from 'lodash';
import classNames from 'classnames';
import { addClickListItemTracking } from 'containers/TrackingProvider';
import { ListItemContentType } from 'constants/tracking/content-types';
import { ContentSource } from 'constants/tracking/content-sources';
import { EDIT_FEATURED_COLLECTION_URL } from 'constants/routes';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { clickEvent, impressionEvent } from 'libs/common/event-tracker/events';
import { reloadPage } from 'libs/utils/window';
import { deleteFeaturedCollection } from 'data/api';
import useTranslate from 'hooks/useTranslate';
import useTracking from 'hooks/useTracking';
import HorizontalScrollArea from 'components/HorizontalScrollArea';
import DropdownMenu from 'components/DropdownMenu';
import DeleteCollectionModal from './DeleteCollectionModal';
import FeaturedItem from './FeaturedItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TrackedFeaturedItem = addClickListItemTracking(FeaturedItem);

const FeaturedCollectionBox = _ref => {
  let {
    id,
    items,
    title,
    viewingSelf,
    allowBump,
    hasItems,
    isActive,
    humanizedTimeLeft
  } = _ref;
  const translate = useTranslate('featured_collection');
  const {
    track
  } = useTracking();
  const seenItems = useRef([]);
  const {
    0: isDeleteModalOpen,
    1: setIsDeleteModalOpen
  } = useState(false);
  useEffect(() => {
    track(impressionEvent({
      id,
      position: 0,
      contentSource: ContentSource.FeaturedCollection,
      contentType: ListItemContentType.FeaturedCollection
    }));
  }, [id, track]);

  const handleItemVisibility = (item, index) => () => {
    if (viewingSelf || includes(seenItems.current, item.id)) return;
    const event = impressionEvent({
      id: item.id,
      position: index + 1,
      contentType: ListItemContentType.Item,
      contentSource: ContentSource.FeaturedCollection,
      itemOwnerId: item.user_id
    });
    track(event);
    seenItems.current = [...seenItems.current, item.id];
  };

  function trackEditCollection() {
    const trackingEvent = clickEvent({
      screen: Screen.CurrentUserProfile,
      target: ClickableElement.EditCollection
    });
    track(trackingEvent);
  }

  function getSubtitleText() {
    if (!viewingSelf) return translate('box_title');
    if (!isActive) return translate('box_inactive_subtitle');
    return humanizedTimeLeft;
  }

  function openDeleteModal() {
    setIsDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteModalOpen(false);
  }

  async function handleDeleteCollection() {
    track(clickEvent({
      target: ClickableElement.DeleteCollection
    }));
    await deleteFeaturedCollection({
      id
    });
    reloadPage();
  }

  function renderEditButton() {
    if (!viewingSelf) return null;

    if (!isActive) {
      const actions = [{
        id: 'edit',
        text: translate('actions.edit'),
        url: EDIT_FEATURED_COLLECTION_URL(id),
        onClick: trackEditCollection
      }, {
        id: 'delete',
        text: translate('actions.delete'),
        onClick: openDeleteModal
      }];
      const dropdownButtonProps = {
        styling: Button.Styling.Flat,
        deprecatedIcon: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "horizontal-dots",
          color: DeprecatedIcon.Color.GreyscaleLevel4,
          testId: "collection-actions-menu-button"
        }),
        size: Button.Size.Medium
      };
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(DropdownMenu, {
          actions: actions,
          buttonProps: dropdownButtonProps
        }), /*#__PURE__*/_jsx(DeleteCollectionModal, {
          isOpen: isDeleteModalOpen,
          onSubmit: handleDeleteCollection,
          onClose: closeDeleteModal
        })]
      });
    }

    return /*#__PURE__*/_jsx(Button, {
      text: translate('actions.edit'),
      styling: Button.Styling.Flat,
      size: Button.Size.Medium,
      url: EDIT_FEATURED_COLLECTION_URL(id),
      onClick: trackEditCollection,
      testId: "featured-collection-box--action-edit"
    });
  }

  function renderEmptyState() {
    return /*#__PURE__*/_jsx(EmptyState, {
      body: translate('empty_state.box.body'),
      action: /*#__PURE__*/_jsx(Button, {
        text: translate('empty_state.box.action'),
        styling: Button.Styling.Filled,
        url: EDIT_FEATURED_COLLECTION_URL(id)
      })
    });
  }

  function renderItems() {
    if (!hasItems) return renderEmptyState();
    return /*#__PURE__*/_jsx("div", {
      className: classNames('u-overflow-hidden', {
        'featured-collection__items--inactive': !isActive
      }),
      children: /*#__PURE__*/_jsx(HorizontalScrollArea, {
        children: items.map((item, index) => /*#__PURE__*/_jsx(Waypoint, {
          onEnter: handleItemVisibility(item, index),
          horizontal: true,
          children: /*#__PURE__*/_jsx(TrackedFeaturedItem, {
            item: item,
            viewingSelf: viewingSelf,
            allowBump: allowBump,
            tracking: {
              id: item.id,
              contentType: ListItemContentType.Item,
              contentSource: ContentSource.FeaturedCollection,
              position: index + 1
            },
            isDescriptionHidden: !isActive
          })
        }, "collection-item-".concat(item.id)))
      })
    });
  }

  return /*#__PURE__*/_jsx("div", {
    className: "featured-collection-container",
    children: /*#__PURE__*/_jsx(Cell, {
      styling: Cell.Styling.Tight,
      testId: "featured-collection",
      children: /*#__PURE__*/_jsxs("div", {
        className: "featured-collection",
        children: [/*#__PURE__*/_jsx(Cell, {
          title: title,
          body: getSubtitleText(),
          suffix: renderEditButton(),
          testId: "featured-collection-box"
        }), renderItems()]
      })
    })
  });
};

export default FeaturedCollectionBox;